import '../App.css';
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    readFilesAndExtractURI,
    changeFileToJPG,
} from '../function/ImageConvertResizer';
import Banner from '../components/Banner';
import Loading from '../components/Loading';
import TitleComponent from '../components/TitleComponent';
import Footer from '../components/Footer';
import yes from '../assets/images/yes.png';
import no from '../assets/images/no.png';

function Offline() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const userId = searchParams.get('userId');
    const secretKey = searchParams.get('secretKey');

    const [photos, setPhotos] = useState([]);
    const [resizedImages, setResizedImages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [state, setState] = useState(0); // 0: upload, 1: info
    const [concepts, setConcepts] = useState([]);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [kind, setKind] = useState('');
    const [isAgree, setIsAgree] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [disabled, setDisabled] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (!userId || !secretKey) {
            alert('잘못된 접근입니다. userId와 secretKey가 필요합니다.');
            navigate('/');
            return;
        }
        loadUserData();
    }, []);

    useEffect(() => {
        if (
            name === '' ||
            email === '' ||
            kind === '' ||
            !isAgree ||
            !isValidEmail(email)
        ) {
            setDisabled(true);
        } else {
            setDisabled(false);
        }
    }, [name, email, kind, isAgree]);

    const loadUserData = async () => {
        setIsLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_ADDRESS}/users/${userId}`;
            const res = await axios.get(url);
            const userData = res.data;

            // concept를 ,로 분리
            if (userData.concept) {
                const conceptArray = userData.concept
                    .split(',')
                    .map((c) => c.trim());
                setConcepts(conceptArray);
            }

            // email, name, kind가 모두 있으면 이미 업데이트된 상태이므로 status 페이지로 이동
            if (userData.email && userData.name && userData.kind) {
                navigate(`/status?userId=${userId}&secretKey=${secretKey}`);
                return;
            }

            // email, name, kind가 빈값이면 그대로 두고, 값이 있으면 기본값으로 설정
            if (userData.email) setEmail(userData.email);
            if (userData.name) setName(userData.name);
            if (userData.kind) {
                // kind가 "dog" 또는 "cat"만 추출 (breed 정보 제거)
                if (userData.kind.includes('dog')) {
                    setKind('dog');
                } else if (userData.kind.includes('cat')) {
                    setKind('cat');
                }
            }

            setIsLoading(false);
        } catch (error) {
            console.error('사용자 데이터 로드 실패:', error);
            alert('사용자 정보를 불러올 수 없습니다.');
            setIsLoading(false);
        }
    };

    const onChange = async (e) => {
        let files = Array.from(e.target.files);

        if (files.length > 0 && files.length < 6) {
            setIsLoading(true);

            // image type 변환
            let jpegFiles = await Promise.all(
                files.map((file) => {
                    return changeFileToJPG(file);
                })
            );

            // 올린 사진들 width, height 중 작은 것을 512로 만들어 리사이징
            let resizedImageArray = await Promise.all(
                jpegFiles.map((file) => {
                    return readFilesAndExtractURI(file);
                })
            );

            setPhotos(resizedImageArray);
            setResizedImages(resizedImageArray);
            setIsLoading(false);
            setState(1);
        } else {
            alert('사진은 1~5장을 업로드하세요');
        }
    };

    const isValidEmail = (email) => {
        return /\S+@\S+\.\S+/.test(email);
    };

    const changeEmail = (e) => {
        if (!isValidEmail(e.target.value)) {
            setEmailError('올바른 형식의 이메일을 입력해주세요');
        } else {
            setEmailError(null);
        }
        setEmail(e.target.value);
    };

    const changeName = (e) => {
        setName(e.target.value);
    };

    const changeKind = (selectedKind, e) => {
        setKind(selectedKind);

        // change the background color of the selected kind
        let kinds = document.getElementsByClassName('kind');
        for (let i = 0; i < kinds.length; i++) {
            kinds[i].style.backgroundColor = 'white';
        }
        e.target.style.backgroundColor = '#F2D7C0';
    };

    const changeIsAgree = (e) => {
        setIsAgree(e.target.checked);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // check everything is filled
        if (name === '' || email === '' || kind === '' || !isAgree) {
            alert('모든 항목을 입력해주세요');
            return;
        }

        if (!isValidEmail(email)) {
            alert('올바른 이메일을 입력해주세요');
            return;
        }

        setIsLoading(true);

        // remove 'data:image/jpeg;base64,' from the beginning of the string
        const base64Images = resizedImages.map((img) =>
            img.replace(/^data:image\/[a-z]+;base64,/, '')
        );

        const url = `${process.env.REACT_APP_API_ADDRESS}/update-offline`;
        const data = {
            userId: userId,
            secretKey: secretKey,
            email: email,
            name: name,
            kind: kind,
            images: base64Images,
        };

        try {
            const res = await axios.post(url, data, {
                'Content-Type': 'application/json',
            });

            // 성공 시 status 페이지로 이동
            navigate(`/status?userId=${userId}&secretKey=${secretKey}`);
        } catch (error) {
            console.error('업데이트 실패:', error);
            alert('정보 업데이트에 실패했습니다. 다시 시도해주세요.');
            setIsLoading(false);
        }
    };

    const conceptDisplayDiv = () => {
        const conceptLabels = {
            studio: '스튜디오',
            job: '직업',
            season: '시즌',
            trip: '여행',
            'water-paint': '수채화',
            'oil-paint': '유화',
        };

        return (
            <>
                <div style={{ fontFamily: 'Suite-regular' }}>
                    <div
                        style={{
                            fontSize: '18px',
                            marginBottom: '20px',
                            textAlign: 'center',
                        }}
                    >
                        선택된 컨셉
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '10px',
                            justifyContent: 'center',
                        }}
                    >
                        {concepts.map((concept, index) => (
                            <div
                                key={index}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#F2D7C0',
                                    borderRadius: '5px',
                                    fontSize: '16px',
                                }}
                            >
                                {conceptLabels[concept] || concept}
                            </div>
                        ))}
                    </div>
                </div>
            </>
        );
    };

    const uploadBannerDiv = () => {
        return (
            <>
                <div>
                    <label htmlFor="upload-photos">
                        <div id="upload-button">+</div>
                    </label>
                    <input
                        id="upload-photos"
                        type="file"
                        multiple
                        onChange={onChange}
                        accept="image/x-png,image/jpeg,image/jpg,image/heic"
                    />
                </div>
            </>
        );
    };

    const infoFormDiv = () => {
        return (
            <>
                <div style={{ fontFamily: 'Suite-regular' }}>
                    <div className="input-field">
                        <div className="input-info">
                            <label>이름</label>{' '}
                            <span>- 반려동물의 이름을 알려주세요</span>
                        </div>
                        <input
                            className="custom-input-text"
                            type="text"
                            value={name}
                            placeholder="스튜디오 컨셉의 경우 영어로 입력해주세요.
                                한글로 입력할 경우 영어로 자동 변환됩니다."
                            onChange={(e) => {
                                changeName(e);
                            }}
                        />
                    </div>

                    <div className="input-field">
                        <div className="input-info">
                            <label>이메일</label>{' '}
                            <span>
                                - 모든 진행 상황은 이메일로 안내해드립니다
                            </span>
                        </div>

                        <input
                            className="custom-input-text"
                            type="email"
                            value={email}
                            onChange={(e) => {
                                changeEmail(e);
                            }}
                        />
                        <div
                            style={{
                                height: '12px',
                                fontWeight: 'bold',
                                color: '#F24646',
                                margin: '3px 8px',
                                fontSize: '12px',
                            }}
                        >
                            {emailError}
                        </div>
                    </div>

                    <div className="input-field">
                        <div className="input-info">
                            <label>대상</label>{' '}
                            <span>- 반려동물의 종을 선택해주세요</span>
                        </div>
                        <div
                            className="kind-buttons"
                            style={{ marginTop: '10px' }}
                        >
                            <div
                                className="white-button kind"
                                style={
                                    kind === 'dog'
                                        ? { backgroundColor: '#F2D7C0' }
                                        : {}
                                }
                                onClick={(e) => {
                                    changeKind('dog', e);
                                }}
                            >
                                강아지
                            </div>
                            <div
                                className="white-button kind"
                                style={
                                    kind === 'cat'
                                        ? {
                                              backgroundColor: '#F2D7C0',
                                              marginLeft: '10px',
                                          }
                                        : { marginLeft: '10px' }
                                }
                                onClick={(e) => {
                                    changeKind('cat', e);
                                }}
                            >
                                고양이
                            </div>
                        </div>
                    </div>

                    <div className="input-field">
                        <div className="input-info">
                            <label>사진</label>{' '}
                            <span>- 업로드된 사진을 확인하세요</span>
                        </div>
                        <div className="upload-image-preview">
                            {resizedImages.map((file, index) => {
                                return (
                                    <img
                                        key={index}
                                        className="preview-image"
                                        src={file}
                                    />
                                );
                            })}
                        </div>
                    </div>

                    <div className="input-field">
                        <input
                            id="agree-checkbox"
                            type="checkbox"
                            checked={isAgree}
                            onChange={(e) => {
                                changeIsAgree(e);
                            }}
                        />
                        <label
                            className="checkbox-label"
                            htmlFor="agree-checkbox"
                        >
                            정보성(광고성 X) 이메일 수신에 동의합니다
                        </label>
                    </div>
                    <div className="center-button">
                        <button
                            disabled={disabled}
                            onClick={(e) => {
                                handleSubmit(e);
                            }}
                        >
                            확인
                        </button>
                    </div>
                </div>
            </>
        );
    };

    const uploadInstructionDiv = () => {
        return (
            <>
                <div style={{ fontFamily: 'Suite-regular' }}>
                    <div style={{ fontSize: '18px' }}>
                        같은 강아지 혹은 고양이의 사진을 1~5장 업로드하세요
                    </div>
                    <div style={{ marginLeft: '10px', marginTop: '10px' }}>
                        <ul style={{ lineHeight: '180%' }}>
                            <li>
                                다양한 각도와 환경일수록 AI가 동물을 이해하는데
                                도움이 됩니다
                            </li>
                            <li>
                                동물이 중앙에 잘 위치하고 사물에 가려지지 않는
                                것이 좋아요
                            </li>
                        </ul>
                        <div
                            style={{ margin: '10px 0', paddingBottom: '20px' }}
                            className="instruction-images"
                        >
                            <div className="instruction-image">
                                <div
                                    style={{
                                        fontWeight: 'bold',
                                        color: '#658354',
                                        fontSize: '20px',
                                    }}
                                >
                                    YES 👍
                                </div>
                                <img src={yes} />
                            </div>
                            <div className="instruction-image">
                                <div
                                    style={{
                                        fontWeight: 'bold',
                                        color: '#cd071e',
                                        fontSize: '20px',
                                    }}
                                >
                                    NONO 👎
                                </div>
                                <img src={no} />
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    const infoInstructionDiv = () => {
        return (
            <>
                <div style={{ fontFamily: 'Suite-regular' }}>
                    <div style={{ fontSize: '18px' }}>
                        <ol style={{ lineHeight: '180%' }}>
                            <li>반려동물의 이름을 입력해주세요.</li>
                            <li>
                                실제{' '}
                                <strong style={{ fontFamily: 'Suite-bold' }}>
                                    사용하는 이메일
                                </strong>
                                을 입력해주세요. 아트 컬렉션의 진행상황과 결과를
                                이메일로 안내해드립니다.
                            </li>
                            <li>
                                동물이 강아지인지 고양이인지 선택해주세요. 그
                                외의 동물들은 아직 지원되지 않습니다.
                            </li>
                        </ol>
                    </div>
                </div>
            </>
        );
    };

    const subtitleDiv = () => {
        return (
            <>
                강아지 혹은 고양이의 사진을 1 ~ 5장 업로드하고 정보를 입력하세요
            </>
        );
    };

    return (
        <div>
            {isLoading ? (
                <Loading />
            ) : (
                <div className="app ">
                    <div className="border-padding">
                        <TitleComponent subtitle={subtitleDiv()} />
                        <div className="page-content">
                            {concepts.length > 0 && (
                                <Banner
                                    bannerTitle={'SELECTED CONCEPT'}
                                    contentDiv={conceptDisplayDiv()}
                                />
                            )}

                            {state === 0 ? (
                                <Banner
                                    bannerTitle={'UPLOAD IMAGE'}
                                    contentDiv={uploadBannerDiv()}
                                />
                            ) : state === 1 ? (
                                <Banner
                                    bannerTitle={'ENTER INFORMATION'}
                                    contentDiv={infoFormDiv()}
                                />
                            ) : null}

                            {state === 0 ? (
                                <Banner
                                    contentDiv={uploadInstructionDiv()}
                                    bannerTitle={'RAYMONG 참고 사항'}
                                />
                            ) : state === 1 ? (
                                <Banner
                                    contentDiv={infoInstructionDiv()}
                                    bannerTitle={'RAYMONG 참고 사항'}
                                />
                            ) : null}
                        </div>
                    </div>

                    <Footer />
                </div>
            )}
        </div>
    );
}

export default Offline;
